<template>
  <div class="base-address-field">
    <div class="base-address-field__row">
      <el-input :value="base.baseAddr" readonly :placeholder="text.baseAddressEmpty" class="base-address-field__addr">
        <el-button slot="append" :disabled="disabled" @click="openPicker">{{ $t('주소 조회') }}</el-button>
      </el-input>
      <el-button v-if="base.baseAddr" size="small" :disabled="disabled" @click="clear">{{
        text.baseAddressClear
      }}</el-button>
    </div>

    <div class="base-address-field__row">
      <el-input
        ref="base_addr_dtl"
        :value="base.baseAddrDtl"
        maxlength="100"
        clearable
        show-word-limit
        :disabled="disabled || !base.baseAddr"
        placeholder="상세 주소"
        @input="onDetail"></el-input>
    </div>

    <div class="base-address-field__row">
      <span class="base-address-field__label">{{ text.maxDeliveryKmLabel }}</span>
      <el-input-number
        :value="maxKmValue"
        :min="minKm"
        :max="maxKm"
        :step="1"
        step-strictly
        :controls="false"
        :disabled="disabled"
        class="base-address-field__km"
        @change="onMaxKm"></el-input-number>
      <span :class="kmInRange ? 'base-address-field__hint' : 'error-text'">{{ kmHint }}</span>
    </div>

    <div class="base-address-field__hint">
      {{ text.baseAddressHint }}
      <span v-if="base.baseAddr">
        · 우편번호 {{ base.baseZipCd || '-' }} · 법정동 {{ base.baseBjdCd || '-' }} · 좌표
        {{ coordinateText }}
      </span>
      <span :class="filterOn ? 'ok-text' : ''">· 거리 조건 {{ filterOn ? '켜짐' : '꺼짐' }}</span>
    </div>
  </div>
</template>

<script>
import { pickAddress } from '@/utils/addressPicker'
import { latestGuard } from '@/utils/latestGuard'
import {
  MAX_DELIVERY_KM_MAX,
  MAX_DELIVERY_KM_MIN,
  PLACE_AREA_TEXT,
  baseAddressFromPicked,
  baseAddressOf,
  clearBaseAddress,
  distanceFilterOn,
  validMaxDeliveryKm,
} from '@/utils/placeServiceArea'

/**
 * 플레이스 **기준 주소 + 최대 거리** 입력 (B7-a 항목 8). `v-model` 은 기준 주소 여섯 필드와
 * `maxDeliveryKm` 이고, 저장은 부모(플레이스 폼)가 한다.
 *
 * 주소는 **위젯으로만** 고친다(입력칸은 읽기 전용) — 손으로 적은 주소는 좌표·법정동 코드와 어긋날 수
 * 있고, 그 어긋남은 자동 편입의 지역·거리 판정을 조용히 틀리게 만든다. 「지우기」가 여섯을 함께 비우는
 * 것도 같은 이유다(주소만 있고 좌표가 없는 반쪽 상태를 만들지 않는다 — 서버 규칙과 같다).
 *
 * **고른 주소는 그 선택을 시작한 편집 세션의 것이다.** 이 컴포넌트는 다이얼로그가 닫혀도 파괴되지 않고
 * 다음 플레이스에 재사용되므로, A 의 주소를 고르고 지오코딩을 기다리는 동안 B 를 열면 A 의 결과가 B 의
 * 값에 합쳐져 올라간다. 판정은 리포의 기존 관용구([latestGuard])가 하고, 무효로 만드는 사건은 셋이다 —
 * 새 선택 · 지우기 · [sessionKey] 교체(부모가 폼을 다시 채웠다). 공용 `addressPicker` 에 전역 취소
 * 상태를 두지 **않는다**: 그것은 같은 순간 열려 있는 다른 화면의 선택까지 죽인다.
 */
export default {
  name: 'PlaceBaseAddressField',

  props: {
    /** 기준 주소 여섯 + `maxDeliveryKm` */
    value: { type: Object, default: null },
    disabled: { type: Boolean, default: false },
    /** 편집 세션 — 부모가 폼을 채울 때마다 올린다. 바뀌면 앞선 주소 선택은 남의 것이 된다 */
    sessionKey: { type: Number, default: 0 },
  },

  data() {
    return {
      text: PLACE_AREA_TEXT,
      minKm: MAX_DELIVERY_KM_MIN,
      maxKm: MAX_DELIVERY_KM_MAX,
      pickGuard: latestGuard(),
    }
  },

  watch: {
    sessionKey() {
      this.pickGuard.invalidate()
    },
  },

  computed: {
    base() {
      return baseAddressOf(this.value)
    },

    /** 서버가 받아 주는 범위인가 — 읽어 온 값은 **클램프하지 않는다**(사람 몰래 값을 고치지 않는다) */
    kmInRange() {
      return validMaxDeliveryKm(this.base.maxDeliveryKm)
    },

    kmHint() {
      return this.kmInRange ? this.text.maxDeliveryKmHint : this.text.maxDeliveryKmOutOfRange
    },

    /**
     * `el-input-number` 에는 **`undefined` 로** 준다 — `null` 은 이 컴포넌트 안에서 `Number(null) = 0`
     * 이 되고 `min` 으로 올려 붙어(`0 <= 1`) 「비움」이 조용히 `1km` 가 된다. 우리 축의 「없음」은 `null`
     * 이고, 그 번역은 경계인 이 자리에서 한 번만 한다.
     */
    maxKmValue() {
      return this.base.maxDeliveryKm === null ? undefined : this.base.maxDeliveryKm
    },

    /** 서버 `distanceFilterOn` 과 같은 식 — 좌표와 상한이 둘 다 있어야 켜진다 */
    filterOn() {
      return distanceFilterOn(this.base)
    },

    coordinateText() {
      if (this.base.baseLat === null || this.base.baseLng === null) return '-'

      return `${this.base.baseLat}, ${this.base.baseLng}`
    },
  },

  methods: {
    async openPicker() {
      const token = this.pickGuard.begin()
      let picked = null
      try {
        picked = await pickAddress({ geocode: true })
      } catch (error) {
        if (this.pickGuard.isLatest(token)) this.$message({ type: 'error', message: error.message })

        return
      }
      // 기다리는 동안 다른 플레이스를 열었거나 주소를 지웠다 — 이 답은 지금 화면의 것이 아니다
      if (!picked || !this.pickGuard.isLatest(token)) return

      this.$emit('input', baseAddressFromPicked(this.base, picked))
      this.$nextTick(() => {
        if (this.$refs.base_addr_dtl) this.$refs.base_addr_dtl.focus()
      })
    },

    clear() {
      this.pickGuard.invalidate()
      this.$emit('input', clearBaseAddress(this.base))
    },

    onDetail(text) {
      this.$emit('input', Object.assign({}, this.base, { baseAddrDtl: text }))
    },

    onMaxKm(km) {
      this.$emit('input', Object.assign({}, this.base, { maxDeliveryKm: km === undefined ? null : km }))
    },
  },
}
</script>

<style scoped>
.base-address-field__row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
}

.base-address-field__addr {
  flex: 1;
}

.base-address-field__label {
  white-space: nowrap;
}

.base-address-field__km {
  width: 120px;
}

.base-address-field__hint {
  color: #909399;
  font-size: 12px;
  line-height: 1.6;
}

.ok-text {
  color: #67c23a;
}

.error-text {
  color: #f56c6c;
  font-size: 12px;
  line-height: 1.6;
}
</style>
